import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '@virtual-care/shadcn';

import { useUser } from '../../firebase';
import type { Speciality } from '../../routes/booking/book-appointment';
import { PriceLine } from '../priceline.tsx';
import SpecialtyList from './specialty-list';

export default function SpecialtySelectionCard({
  specialties,
  selectedId,
  onSelect,
  searchQuery,
  onSearchChange,
  fee,
}: {
  specialties: Speciality[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  fee?: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: identity } = useUser();

  return (
    <div className="card-container">
      <Card className="card">
        <CardHeader className="text-center">
          <CardTitle className="text-xl font-bold">
            {t('booking.specialtySelection.title')}
          </CardTitle>
          <p className="text-sm text-gray-500">
            {t('booking.specialtySelection.subtitle')}
          </p>
        </CardHeader>

        <CardContent className="flex flex-col gap-5">
          <div className="relative mx-3 md:mx-6">
            <Search className="absolute inset-s-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder={t('booking.specialtySelection.searchPlaceholder')}
              className="rounded-4xl py-6 ps-10"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {identity?.userType !== 'B2B' && (
            <PriceLine value={fee} className="mx-3 md:mx-6" />
          )}

          <SpecialtyList
            specialties={specialties}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        </CardContent>
        <CardFooter className="flex w-full justify-center gap-4">
          <Button
            type="button"
            variant={'outline'}
            onClick={() => navigate(-1)}
            className="secondary-button"
          >
            {t('booking.specialtySelection.back')}
          </Button>
          <Button
            variant={'default'}
            disabled={!selectedId}
            onClick={() => {
              navigate(`${selectedId}/symptom-checker`);
            }}
            className="primary-button"
          >
            {t('booking.specialtySelection.continue')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
